// The composer's attachments: each picked file is prepared (photos
// downscaled, video location removed) and uploaded at once, two at a time,
// with progress, cancel and retry. A message goes out once all are uploaded.
// Browser work (canvas, XHR) comes in through `TrayDeps`, so this is tested in node.
import { pickFiles, type Media, type MediaKind, type MediaLimits } from "./media";

/** What a prepared file uploads as. */
export interface Prepared {
  blob: Blob;
  kind: MediaKind;
  /** `name` for files; width/height/duration hints for video. */
  query: { name?: string; width?: number; height?: number; duration_ms?: number };
  contentType: string;
  /** A video's poster frame, uploaded after it. */
  poster: Blob | null;
}

export interface TrayDeps {
  prepare: (file: File, kind: MediaKind) => Promise<Prepared>;
  /** Uploads one prepared file (and its poster), reporting progress 0–1. */
  upload: (prepared: Prepared, onProgress: (fraction: number) => void, signal: AbortSignal) => Promise<Media>;
  /** A local preview for a photo (an object URL), or null. */
  preview?: (file: File, kind: MediaKind) => string | null;
  revoke?: (url: string) => void;
  /** Turns a failure into text for the item. */
  describe?: (error: unknown) => string;
}

export interface TrayItem {
  key: string;
  kind: MediaKind;
  name: string;
  size: number;
  previewUrl: string | null;
  status: "waiting" | "preparing" | "uploading" | "done" | "failed";
  /** Upload progress, 0–1. */
  progress: number;
  media: Media | null;
  error: string | null;
}

export interface TraySnapshot {
  items: readonly TrayItem[];
  /** Why files were turned away, until the next pick. */
  notice: string | null;
}

/** Uploads under way at once (the server streams at most three per user). */
export const TRAY_CONCURRENCY = 2;

/** Ready to send: something attached and everything uploaded. */
export function trayReady(items: readonly TrayItem[]): boolean {
  return items.length > 0 && items.every((item) => item.status === "done");
}

/** "Uploading 2 of 3 · 40%" while anything is still going up. */
export function trayStatus(items: readonly TrayItem[]): string | null {
  const busy = items.filter((item) => item.status !== "done" && item.status !== "failed");
  if (busy.length === 0) return null;
  const total = items.filter((item) => item.status !== "failed");
  const progress = total.reduce((sum, item) => sum + (item.status === "done" ? 1 : item.progress), 0) / total.length;
  return `Uploading ${total.length - busy.length + 1} of ${total.length} · ${Math.round(progress * 100)}%`;
}

export class AttachmentTray {
  private snapshot: TraySnapshot = { items: [], notice: null };
  private readonly listeners = new Set<() => void>();
  private readonly files = new Map<string, File>();
  private readonly controllers = new Map<string, AbortController>();
  private nextKey = 0;
  private closed = false;

  constructor(private readonly deps: TrayDeps) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): TraySnapshot => this.snapshot;

  /** Adds picked, pasted or dropped files (as many as fit). */
  add(files: readonly File[], limits: MediaLimits): void {
    if (this.closed || files.length === 0) return;
    const { accepted, notice } = pickFiles(files, this.snapshot.items.length, limits);
    const items = accepted.map(({ file, kind }): TrayItem => {
      const key = `a${++this.nextKey}`;
      this.files.set(key, file);
      return {
        key,
        kind,
        name: file.name,
        size: file.size,
        previewUrl: kind === "image" ? (this.deps.preview?.(file, kind) ?? null) : null,
        status: "waiting",
        progress: 0,
        media: null,
        error: null,
      };
    });
    this.set({ items: [...this.snapshot.items, ...items], notice });
    this.pump(limits);
  }

  /** Cancels an upload (or drops a finished one); the server deletes unsent uploads after a day. */
  remove(key: string): void {
    this.controllers.get(key)?.abort();
    this.forget(key);
    this.set({ ...this.snapshot, items: this.snapshot.items.filter((item) => item.key !== key) });
  }

  retry(key: string, limits: MediaLimits): void {
    this.update(key, (item) =>
      item.status === "failed" ? { ...item, status: "waiting", progress: 0, error: null } : item,
    );
    this.pump(limits);
  }

  dismissNotice(): void {
    if (this.snapshot.notice) this.set({ ...this.snapshot, notice: null });
  }

  /** The uploaded media, in order, once all are done; else null. */
  ready(): Media[] | null {
    return trayReady(this.snapshot.items) ? this.snapshot.items.map((item) => item.media!) : null;
  }

  /** Empties the tray after a send (the uploads now belong to the message). */
  clear(): void {
    for (const key of [...this.files.keys()]) this.forget(key);
    this.set({ items: [], notice: null });
  }

  /** Takes files again after `close` (React may remount). */
  open(): void {
    this.closed = false;
  }

  /** Cancels everything; the tray takes nothing more until opened again. */
  close(): void {
    this.closed = true;
    for (const controller of this.controllers.values()) controller.abort();
    this.clear();
  }

  private forget(key: string): void {
    this.controllers.delete(key);
    this.files.delete(key);
    const url = this.snapshot.items.find((item) => item.key === key)?.previewUrl;
    if (url) this.deps.revoke?.(url);
  }

  private pump(limits: MediaLimits): void {
    let running = this.snapshot.items.filter((i) => i.status === "preparing" || i.status === "uploading").length;
    for (const item of this.snapshot.items) {
      if (running >= TRAY_CONCURRENCY) break;
      if (item.status !== "waiting") continue;
      running++;
      void this.run(item.key, limits);
    }
  }

  private async run(key: string, limits: MediaLimits): Promise<void> {
    const file = this.files.get(key);
    const item = this.snapshot.items.find((i) => i.key === key);
    if (!file || !item) return;
    const controller = new AbortController();
    this.controllers.set(key, controller);
    this.update(key, (i) => ({ ...i, status: "preparing" }));
    try {
      const prepared = await this.deps.prepare(file, item.kind);
      if (controller.signal.aborted) return;
      const max =
        prepared.kind === "image"
          ? limits.image_max_bytes
          : prepared.kind === "video"
            ? limits.video_max_bytes
            : limits.file_max_bytes;
      if (prepared.blob.size > max) throw new Error("It's too large to send");
      this.update(key, (i) => ({ ...i, status: "uploading", kind: prepared.kind }));
      const media = await this.deps.upload(
        prepared,
        (fraction) => this.update(key, (i) => (i.status === "uploading" ? { ...i, progress: fraction } : i)),
        controller.signal,
      );
      if (controller.signal.aborted) return;
      this.update(key, (i) => ({ ...i, status: "done", progress: 1, media }));
    } catch (error) {
      if (controller.signal.aborted) return;
      const text = this.deps.describe?.(error) ?? (error instanceof Error ? error.message : "Upload failed");
      this.update(key, (i) => ({ ...i, status: "failed", error: text }));
    } finally {
      if (this.controllers.get(key) === controller) this.controllers.delete(key);
      if (!this.closed) this.pump(limits);
    }
  }

  private update(key: string, change: (item: TrayItem) => TrayItem): void {
    let changed = false;
    const items = this.snapshot.items.map((item) => {
      if (item.key !== key) return item;
      const next = change(item);
      if (next !== item) changed = true;
      return next;
    });
    if (changed) this.set({ ...this.snapshot, items });
  }

  private set(snapshot: TraySnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) listener();
  }
}
